import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Document,
  Packer,
  Paragraph,
  AlignmentType,
  HeadingLevel,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ImageRun,
  Header,
  BorderStyle,
} from 'docx';

// hooks
import { usePosthogCapture } from 'eCoach/posthogCapture/formFilling';
import { useUser } from 'shared/hooks/api/user';

// components
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import StyledMarkdown from 'shared/StyledMarkdown';
import ChatBubble from 'eCoach/ChatBubble';

// Helper components
import RAGCard, { RAGOutput } from 'eCoach/RAGCard';

// assets
import lverLogo from 'eCoach/_assets/lver-logo.png';

interface WarningLetterTemplateProps {
  output: {
    document_url?: string;
    document_name?: string;
    message?: string;
    content?: string; // Optional markdown content for the letter
  };
}

export default function WarningLetterTemplate({
  output,
}: WarningLetterTemplateProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const formFilling = usePosthogCapture();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedDocument, setGeneratedDocument] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const { data: user } = useUser();

  // Extract data from output
  const documentName = output?.document_name || 'warning-letter.docx';
  const displayMessage =
    output?.message ||
    t('Your warning letter has been generated. You can download it below.');

  // Function to decode HTML entities
  const decodeHTMLEntities = (text: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  // Content for the warning letter
  const rawContent =
    output?.content ||
    displayMessage ||
    `# WARNING LETTER

${new Date().toLocaleDateString()}

${user?.organization?.name || t('Company Name')}

Dear ${user?.first_name || t('Employee')},

This is an official warning letter. Please consider this as a formal notice regarding workplace conduct.

Please acknowledge receipt of this letter.

Sincerely,

Management`;

  // Decode HTML entities in the content
  const letterContent = decodeHTMLEntities(rawContent);

  // Function to convert markdown to docx document
  const generateWarningLetter = useCallback(async () => {
    try {
      setGenerating(true);
      // Parse the markdown into a document structure
      const lines = letterContent.split('\n');
      const docElements = [];

      // Helper function to parse italic text
      const parseItalicText = (text: string): TextRun[] => {
        const textRuns: TextRun[] = [];
        const italicRegex = /\*(.*?)\*/g;
        let lastIndex = 0;
        let match = italicRegex.exec(text);

        while (match !== null) {
          // Add text before the match
          if (match.index > lastIndex) {
            const beforeText = text.substring(lastIndex, match.index);
            if (beforeText) {
              textRuns.push(new TextRun({ text: beforeText }));
            }
          }

          // Add the italic text
          textRuns.push(
            new TextRun({
              text: match[1],
              italics: true,
            })
          );

          lastIndex = italicRegex.lastIndex;
          match = italicRegex.exec(text);
        }

        // Add any remaining text
        if (lastIndex < text.length) {
          const afterText = text.substring(lastIndex);
          if (afterText) {
            textRuns.push(new TextRun({ text: afterText }));
          }
        }

        return textRuns.length > 0 ? textRuns : [new TextRun({ text })];
      };

      // Process markdown text and create TextRun elements
      const parseMarkdownText = (text: string) => {
        const textRuns: TextRun[] = [];
        const remainingText = text;

        // Process bold text - **text** (do this first to handle bold italic combinations)
        const boldRegex = /\*\*\*(.*?)\*\*\*|\*\*(.*?)\*\*/g;
        let lastIndex = 0;
        let match = boldRegex.exec(remainingText);

        while (match !== null) {
          // Add text before the match
          if (match.index > lastIndex) {
            const beforeText = remainingText.substring(lastIndex, match.index);
            textRuns.push(...parseItalicText(beforeText));
          }

          // Add the formatted text
          const textContent = match[1] || match[2]; // Triple or double asterisk content
          if (match[1]) {
            // Triple asterisk = bold + italic
            textRuns.push(
              new TextRun({
                text: textContent,
                bold: true,
                italics: true,
              })
            );
          } else {
            // Double asterisk = bold
            textRuns.push(
              new TextRun({
                text: textContent,
                bold: true,
              })
            );
          }

          lastIndex = boldRegex.lastIndex;
          match = boldRegex.exec(remainingText);
        }

        // Add any remaining text after the last match
        if (lastIndex < remainingText.length) {
          const afterText = remainingText.substring(lastIndex);
          textRuns.push(...parseItalicText(afterText));
        }

        return textRuns.length > 0 ? textRuns : [new TextRun({ text })];
      };

      // Helper function to parse markdown tables
      const parseMarkdownTable = (tableLines: string[]) => {
        if (tableLines.length < 2) return null; // Need at least header and separator

        const rows = [];

        // Parse header row
        const headerCells = tableLines[0]
          .split('|')
          .map((cell) => cell.trim())
          .filter((cell) => cell !== '');

        if (headerCells.length === 0) return null;

        // Create header row
        rows.push(
          new TableRow({
            children: headerCells.map(
              (cellText) =>
                new TableCell({
                  children: [
                    new Paragraph({
                      children: parseMarkdownText(cellText),
                    }),
                  ],
                })
            ),
          })
        );

        // Parse data rows (skip separator row at index 1)
        for (let i = 2; i < tableLines.length; i += 1) {
          const line = tableLines[i].trim();
          if (line && line.includes('|')) {
            const cells = line
              .split('|')
              .map((cell) => cell.trim())
              .filter((cell) => cell !== '');

            if (cells.length > 0) {
              // Pad with empty cells if needed to match header length
              while (cells.length < headerCells.length) {
                cells.push('');
              }

              rows.push(
                new TableRow({
                  children: cells.slice(0, headerCells.length).map(
                    (cellText) =>
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: parseMarkdownText(cellText),
                          }),
                        ],
                      })
                  ),
                })
              );
            }
          }
        }

        // Check if this is the confidentiality header table (2 columns, contains confidentiality text)
        const isConfidentialityHeader =
          headerCells.length === 2 &&
          (headerCells[0].includes('PRIVÉ ET CONFIDENTIEL') ||
            headerCells[0].includes('PRIVATE AND CONFIDENTIAL'));

        if (isConfidentialityHeader) {
          // Create borderless table for confidentiality header
          return new Table({
            rows,
            width: {
              size: 100,
              type: WidthType.PERCENTAGE,
            },
            borders: {
              top: { style: BorderStyle.NONE, size: 0, color: 'ffffff' },
              bottom: { style: BorderStyle.NONE, size: 0, color: 'ffffff' },
              left: { style: BorderStyle.NONE, size: 0, color: 'ffffff' },
              right: { style: BorderStyle.NONE, size: 0, color: 'ffffff' },
              insideHorizontal: {
                style: BorderStyle.NONE,
                size: 0,
                color: 'ffffff',
              },
              insideVertical: {
                style: BorderStyle.NONE,
                size: 0,
                color: 'ffffff',
              },
            },
          });
        }

        return new Table({
          rows,
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
        });
      };

      // Parse lines, handling multi-line constructs like tables
      let i = 0;
      while (i < lines.length) {
        const trimmedLine = lines[i].trim();

        // Check for consecutive empty lines and apply proportional spacing
        if (!trimmedLine) {
          let emptyLineCount = 0;
          let j = i;

          // Count consecutive empty lines
          while (j < lines.length && !lines[j].trim()) {
            emptyLineCount += 1;
            j += 1;
          }

          // Apply spacing based on number of consecutive empty lines
          if (emptyLineCount > 0) {
            const spacingAmount = Math.min(emptyLineCount * 100, 300); // Cap at 300
            docElements.push(
              new Paragraph({
                spacing: {
                  before: spacingAmount,
                },
              })
            );
          }

          i = j; // Skip all the empty lines we just processed
        } else if (trimmedLine.startsWith('|') && trimmedLine.includes('|')) {
          // Check for markdown table (starts with |)
          const tableLines = [];
          let j = i;

          // Collect all consecutive table lines
          while (j < lines.length) {
            const currentLine = lines[j].trim();
            if (currentLine.startsWith('|') && currentLine.includes('|')) {
              tableLines.push(currentLine);
              j += 1;
            } else if (currentLine === '' && tableLines.length > 0) {
              // Allow empty lines within tables
              j += 1;
            } else {
              break;
            }
          }

          // Parse and add the table if we have enough lines
          if (tableLines.length >= 2) {
            const table = parseMarkdownTable(tableLines);
            if (table) {
              docElements.push(table);
              docElements.push(
                new Paragraph({
                  spacing: {
                    before: 200,
                    after: 200,
                  },
                })
              );
            }
          }

          i = j; // Skip to after the table
        } else if (/^[-_*]{3,}$/.test(trimmedLine)) {
          // Check for horizontal rule (---, ___, ***)
          docElements.push(
            new Paragraph({
              text: '_______________________________________________________________________________',
              border: {
                bottom: {
                  color: 'auto',
                  space: 1,
                  style: 'single',
                  size: 6,
                },
              },
              spacing: {
                before: 300,
                after: 300,
              },
            })
          );
          i += 1;
        } else if (trimmedLine.startsWith('# ')) {
          // Heading 1
          docElements.push(
            new Paragraph({
              children: parseMarkdownText(trimmedLine.substring(2)),
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 400,
              },
            })
          );
          i += 1;
        } else if (trimmedLine.startsWith('## ')) {
          // Heading 2
          docElements.push(
            new Paragraph({
              children: parseMarkdownText(trimmedLine.substring(3)),
              heading: HeadingLevel.HEADING_2,
              spacing: {
                after: 300,
              },
            })
          );
          i += 1;
        } else {
          // Regular paragraph
          docElements.push(
            new Paragraph({
              children: parseMarkdownText(trimmedLine),
              spacing: {
                after: 100, // Small spacing between paragraphs for readability
              },
            })
          );
          i += 1;
        }
      }

      // Create header with logo
      let headerElement = null;
      try {
        // Fetch the logo image and convert to array buffer
        const response = await fetch(lverLogo);
        const arrayBuffer = await response.arrayBuffer();
        const logoData = new Uint8Array(arrayBuffer);

        headerElement = new Header({
          children: [
            new Paragraph({
              children: [
                new ImageRun({
                  data: logoData,
                  transformation: {
                    width: 161,
                    height: 49,
                  },
                  type: 'png',
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 200,
              },
            }),
          ],
        });
      } catch (logoError) {
        console.warn('Failed to load logo for header:', logoError);
        // Create empty header if logo fails to load
        headerElement = new Header({
          children: [
            new Paragraph({
              text: '',
            }),
          ],
        });
      }

      // Create a new document with the parsed elements and header
      const doc = new Document({
        sections: [
          {
            properties: {},
            headers: {
              default: headerElement,
            },
            children: docElements,
          },
        ],
      });

      // Generate the document as a blob
      const blob = await Packer.toBlob(doc);

      // Create a blob URL that can be used by RAGCard
      const blobUrl = URL.createObjectURL(blob);
      setGeneratedDocument({
        name: documentName,
        url: blobUrl,
      });

      // Track document generation
      formFilling.warningLetterGenerated();

      return true;
    } catch (err) {
      console.error('Error generating document:', err);
      setError(
        t('There was an error generating the document. Please try again.')
      );
      return false;
    } finally {
      setGenerating(false);
    }
  }, [documentName, letterContent, formFilling, t]);

  // Auto-generate the document when there's no URL from the backend
  useEffect(() => {
    if (!output?.document_url && !generatedDocument && !generating) {
      generateWarningLetter();
    }
  }, [
    output?.document_url,
    generatedDocument,
    generating,
    generateWarningLetter,
  ]);

  // Prepare RAG output based on the available document
  const getRAGOutput = (): RAGOutput => {
    // If we have a generated document, use it
    if (generatedDocument) {
      return {
        rag: {
          summary: displayMessage,
          document_translations: [
            {
              language: 'en',
              parent: {
                id: 'warning-letter-doc',
                use_source_url: true,
              },
              url: generatedDocument.url,
              title: generatedDocument.name,
              source_url: generatedDocument.url,
            },
          ],
        },
      };
    }

    // If we have a document URL from the backend, use it
    if (output?.document_url) {
      return {
        rag: {
          summary: displayMessage,
          document_translations: [
            {
              language: 'en',
              parent: {
                id: 'warning-letter-doc',
                use_source_url: true,
              },
              url: output.document_url,
              title: documentName,
              source_url: output.document_url,
            },
          ],
        },
      };
    }

    // Return empty documents array when still generating
    return {
      rag: {
        summary: generating
          ? t('Generating your warning letter...')
          : displayMessage,
        document_translations: [],
      },
    };
  };

  return (
    <>
      {error ? (
        <ChatBubble>
          <StyledMarkdown content={displayMessage} />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </ChatBubble>
      ) : (
        <RAGCard output={getRAGOutput()} />
      )}
      {generating && (
        <ChatBubble>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CircularProgress size={20} />
            <StyledMarkdown content={t('Generating document...')} />
          </div>
        </ChatBubble>
      )}
    </>
  );
}
